<template>
  <nav class="navbar navbar-expand-lg navbar-light bg-white sticky-top p-2">
    <div class="container">
      <!-- Navbar Brand -->
      <a class="navbar-brand" href="/">
        <h1 class="sitename">
          {{ $t("common.siteName") }}<span class="text-danger">.</span>
        </h1>
      </a>

      <!--
        Mobile header keeps only the language globe, login and the toggler.
        Download moved into the collapsed menu below: it is a long label and a
        secondary action on a phone, and four controls beside the wordmark left
        the bar cramped.
      -->
      <div class="d-flex align-items-center gap-2 d-lg-none">
        <LanguageSwitcher compact />

        <a
          class="btn icon-btn"
          :aria-label="$t('common.login')"
          :title="$t('common.login')"
          :href="appUrl"
        >
          <i class="bi bi-person icon"></i>
        </a>

        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          :aria-label="$t('nav.toggle')"
        >
          <span class="navbar-toggler-icon"></span>
        </button>
      </div>

      <!-- Navbar Links -->
      <div
        class="collapse navbar-collapse justify-content-center text-muted"
        id="navbarNav"
      >
        <ul class="navbar-nav nav-left">
          <li class="nav-item pe-2">
            <a :class="activeClass('home')" class="nav-link" href="/">{{
              $t("nav.home")
            }}</a>
          </li>
          <li class="nav-item pe-2">
            <a :class="activeClass('about')" class="nav-link" href="/about">{{
              $t("nav.about")
            }}</a>
          </li>
          <li class="nav-item dropdown pe-2">
            <a
              :class="initiativesActiveClass"
              class="nav-link dropdown-toggle"
              href="#"
              id="initiativesDropdown"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              >{{ $t("nav.initiatives") }}</a
            >
            <ul class="dropdown-menu" aria-labelledby="initiativesDropdown">
              <li>
                <a
                  :class="dropdownActiveClass('tamil-data-decentralization')"
                  class="dropdown-item"
                  href="/tamil-data-decentralization"
                >
                  <i class="bi bi-diagram-3 me-2"></i
                  >{{ $t("nav.decentralization") }}
                </a>
              </li>
              <li>
                <a
                  :class="dropdownActiveClass('tamil-language-research-fund')"
                  class="dropdown-item"
                  href="/tamil-language-research-fund"
                >
                  <i class="bi bi-mortarboard me-2"></i
                  >{{ $t("nav.researchFund") }}
                </a>
              </li>
              <li>
                <a
                  :class="dropdownActiveClass('reward-for-tamil-kids-name')"
                  class="dropdown-item"
                  href="/reward-for-tamil-kids-name"
                >
                  <i class="bi bi-gift me-2"></i>{{ $t("nav.rewardName") }}
                </a>
              </li>
              <li>
                <a
                  :class="dropdownActiveClass('fourth-child-support')"
                  class="dropdown-item"
                  href="/fourth-child-support"
                >
                  <i class="bi bi-people me-2"></i>{{ $t("nav.fourthChild") }}
                </a>
              </li>
            </ul>
          </li>
          <li class="nav-item pe-2">
            <a
              :class="activeClass('archive')"
              class="nav-link"
              href="/archive"
              >{{ $t("nav.archive") }}</a
            >
          </li>
          <li class="nav-item pe-2">
            <a
              :class="activeClass('contact')"
              class="nav-link"
              href="/contact"
              >{{ $t("nav.contact") }}</a
            >
          </li>
        </ul>

        <!-- Download, shown inside the collapsed menu on small screens -->
        <div class="d-lg-none mobile-actions">
          <a class="btn btn-danger w-100" :href="downloadUrl">
            <i class="bi bi-cloud-download me-2"></i>
            {{ $t("common.downloadClient") }}
          </a>
        </div>

        <!-- Desktop actions -->
        <div class="d-none d-lg-flex align-items-center gap-2 ms-auto">
          <LanguageSwitcher />

          <a class="btn btn-success text-center" :href="appUrl">
            <i class="bi bi-person icon"></i> {{ $t("common.login") }}
          </a>

          <a class="btn btn-danger text-center" :href="downloadUrl">
            <i class="bi bi-cloud-download icon"></i>
            {{ $t("common.downloadClient") }}
          </a>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";

const INITIATIVE_ROUTES = [
  "tamil-data-decentralization",
  "tamil-language-research-fund",
  "reward-for-tamil-kids-name",
  "fourth-child-support",
];

/** Windows desktop client. Referenced from both the mobile and desktop CTAs. */
const DOWNLOAD_URL =
  "https://s3.ap-southeast-1.amazonaws.com/static.tamiltoken.org/downloads/tamilcrypto-app-win32-x64.zip";

const APP_URL = "https://app.tamildata.org";

export default {
  name: "NavBar",
  components: {
    LanguageSwitcher,
  },
  data() {
    return {
      downloadUrl: DOWNLOAD_URL,
      appUrl: APP_URL,
    };
  },
  computed: {
    initiativesActiveClass() {
      return INITIATIVE_ROUTES.includes(this.$route.name) ? "active" : "";
    },
  },
  methods: {
    activeClass(name) {
      return this.$route.name === name ? "active" : "";
    },
    dropdownActiveClass(name) {
      return this.$route.name === name ? "is-active" : "";
    },
  },
};
</script>

<style scoped>
.sitename {
  font-weight: bold;
}

.btn {
  border-radius: 50px;
  font-size: 14px;
  white-space: nowrap;
}

.text-danger {
  color: red;
  display: inline;
}

.navbar-nav .nav-link.active {
  font-weight: bold;
  color: #bb2131;
  border-bottom: 2px solid #dc3545;
  display: inline-block;
}

/* Initiatives dropdown */
.dropdown-menu {
  border: none;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
  padding: 8px;
  font-size: 15px;
}

.dropdown-item {
  border-radius: 8px;
  padding: 8px 12px;
  color: #7f7f90;
}

.dropdown-item:hover,
.dropdown-item:focus {
  background: rgba(206, 18, 18, 0.08);
  color: #ce1212;
}

.dropdown-item.is-active {
  font-weight: bold;
  color: #bb2131;
  background: rgba(206, 18, 18, 0.08);
}

/* Center nav links in web view */
@media (min-width: 992px) {
  .nav-left {
    width: 100%;
    display: flex;
    justify-content: center;
  }
}

/* Mobile View */
@media (max-width: 991px) {
  .navbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  /* Align nav links to the left */
  .nav-left {
    text-align: left;
    width: 100%;
    padding-left: 10px;
  }

  .nav-left .nav-item {
    text-align: left;
  }

  /* Circular icon button in the mobile header (login) */
  .icon-btn {
    background: #dc3545;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    padding: 0;
    flex-shrink: 0;
  }

  .icon-btn i {
    font-size: 20px;
  }

  .icon-btn:hover,
  .icon-btn:focus {
    background: #a30f0f;
    color: #fff;
  }

  .navbar-toggler {
    border: none;
    padding: 4px 8px;
  }

  /* Download CTA sits at the end of the collapsed menu */
  .mobile-actions {
    padding: 12px 10px 4px;
    margin-top: 8px;
    border-top: 1px solid #f0f0f4;
  }
}
</style>
